import { asRecord } from "./content-helpers";
import {
	BLOCK_KIND_TYPES,
	type BlockInteractionSpec,
	type BlockKindDefinition,
	type BlockKindType,
	type BlockPartitionRole,
} from "./define-block-kind";
import {
	codeBlockKind,
	imageBlockKind,
	motionGraphicBlockKind,
	statisticBlockKind,
	videoBlockKind,
} from "./kinds/media-code-statistic";
import {
	comparisonBlockKind,
	pollBlockKind,
	reflectionBlockKind,
	revealBlockKind,
} from "./kinds/engagement";
import {
	dividerBlockKind,
	processBlockKind,
	quizBlockKind,
} from "./kinds/structure";
import { calloutBlockKind, textBlockKind } from "./kinds/text-and-callout";

/** The Block Kind Registry: exactly one definition per `BlockType`. */
export const BLOCK_KIND_REGISTRY = {
	TEXT: textBlockKind,
	IMAGE: imageBlockKind,
	VIDEO: videoBlockKind,
	CODE: codeBlockKind,
	CALLOUT: calloutBlockKind,
	DIVIDER: dividerBlockKind,
	STATISTIC: statisticBlockKind,
	PROCESS: processBlockKind,
	QUIZ: quizBlockKind,
	POLL: pollBlockKind,
	MOTION_GRAPHIC: motionGraphicBlockKind,
	REVEAL: revealBlockKind,
	COMPARISON: comparisonBlockKind,
	REFLECTION: reflectionBlockKind,
} as const satisfies Record<BlockKindType, BlockKindDefinition>;

export function isBlockKindType(value: string): value is BlockKindType {
	return (BLOCK_KIND_TYPES as readonly string[]).includes(value);
}

/** Definition for a block type; throws on an unknown type (fail closed). */
export function getBlockKind(type: string): BlockKindDefinition {
	const key = type.toUpperCase();
	if (!isBlockKindType(key)) {
		throw new Error(`Unknown block kind: ${type}`);
	}
	return BLOCK_KIND_REGISTRY[key] as BlockKindDefinition;
}

/** Raw persisted/generated content → the kind's canonical content. */
export function normalizeBlockContent(
	type: string,
	raw: unknown,
): Record<string, unknown> {
	return getBlockKind(type).normalizeContent(asRecord(raw));
}

/** Speakable narration text for one block ("" for unknown kinds, which carry no narration). */
export function deriveBlockNarrationText(block: {
	type: string;
	content: unknown;
}): string {
	const key = block.type.toUpperCase();
	if (!isBlockKindType(key)) {
		return "";
	}
	return BLOCK_KIND_REGISTRY[key].deriveNarrationText(
		asRecord(block.content),
	);
}

/** A block's partition role (content-aware); null for unknown kinds. */
export function resolveBlockPartitionRole(block: {
	type: string;
	content: unknown;
}): BlockPartitionRole | null {
	const key = block.type.toUpperCase();
	if (!isBlockKindType(key)) {
		return null;
	}
	const kind: BlockKindDefinition = BLOCK_KIND_REGISTRY[key];
	return kind.resolvePartitionRole
		? kind.resolvePartitionRole(asRecord(block.content))
		: kind.partitionRole;
}

/** The interaction spec a block's kind declares for `kind`; null when it accepts none. */
export function getBlockInteractionSpec(
	type: string,
	kind: string,
): BlockInteractionSpec | null {
	const key = type.toUpperCase();
	if (!isBlockKindType(key)) {
		return null;
	}
	const kindDefinition: BlockKindDefinition = BLOCK_KIND_REGISTRY[key];
	return (
		kindDefinition.interactions.find((spec) => spec.kind === kind) ?? null
	);
}

/** Weight toward the per-card stackable text cap (0 for unknown kinds). */
export function getBlockTextWeight(type: string): number {
	const key = type.toUpperCase();
	return isBlockKindType(key) ? BLOCK_KIND_REGISTRY[key].textWeight : 0;
}
